import clsx from 'clsx';
import { ComponentPropsWithoutRef, Dispatch, FC, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { CashAccountType, InsurancePolicySubtype, InvestmentType, LoanType } from '../../generated/graphql';
import { displayCashAccountType, isCashAccountType } from '../../CashAccount';
import { displayInvestmentType, isInvestmentType } from '../../Investment';
import { displayLoanType, isLoanType } from '../../Loan';
import { displayInsurancePolicySubType } from '../../Insurance';
import { RadioButtonSolidIcon } from '../../assets';
import { isDarkTheme } from '../../util';

interface AccountGroupingsProps
  extends Pick<ComponentPropsWithoutRef<'ul'>, 'className'> {
  title: string;
  items: Array<
    CashAccountType | InvestmentType | LoanType | InsurancePolicySubtype
  >;
  selected?: string;
  setSelected: Dispatch<SetStateAction<CashAccountType | InvestmentType | LoanType | InsurancePolicySubtype | undefined>>;
}

export const AccountGroupings: FC<AccountGroupingsProps> = ({
  items,
  title,
  selected = '',
  setSelected,
}) => {
  const { t: tLoan } = useTranslation('loan');
  const { t: tInsurance } = useTranslation('insurance');
  const { t: tInvestment } = useTranslation('investment');
  const { t: tCashAccount } = useTranslation('cashAccount');

  function determineText(
    item: CashAccountType | InvestmentType | LoanType | InsurancePolicySubtype
  ) {
    let text = '';
    if (isCashAccountType(item)) {
      text = displayCashAccountType(item as CashAccountType, tCashAccount);
    } else if (isInvestmentType(item)) {
      text = displayInvestmentType(item as InvestmentType, tInvestment);
    } else if (isLoanType(item)) {
      text = displayLoanType(item as LoanType, tLoan);
    } else {
      text = displayInsurancePolicySubType(item as InsurancePolicySubtype, tInsurance);
    }
    return text;
  }

  return (
    <div>
      <h1 className="mt-[45px] mb-4 font-bold text-darkMode-gray-medium">{title}</h1>
      <ul className="flex flex-col sm:flex-row sm:flex-wrap">
        {items.map((item) => (
          <li
            role="button"
            style={{ alignItems: 'inherit' }}
            onClick={() => setSelected(item)}
            className="flex min-h-[48px] sm:w-1/2 select-none items-center py-3"
            key={item}
          >
            <RadioButtonSolidIcon
              className={clsx('self-baseline', item === selected ? 'text-palette-600' : 'text-darkMode-gray-medium')}
              checked={item === selected}
            />
            <span className={clsx('ml-4 text-lg leading-6', isDarkTheme() ? 'text-white' : 'text-darkMode-gray-dark')}>{determineText(item)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};
